import { memo, useCallback, useEffect } from 'react';
import { Text, View, Image, StyleSheet, Linking } from 'react-native';

import { I18n, ActivityOverlay, Utils, Container } from '../components';
import { COLOR, FONT, FONT_SIZE, THEME, __WEB__, __ANDROID__ } from '../../constants/styles';

import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { acceptOrRejectAFTerms } from './actions';

import BackgroundImageContainer from './background_image_container';
import PrivacyButton from './privacy_button';
import CommonStyles from './common_styles';
import { SetupMFA, EnterMFA } from './mfa_views';

const appleStoreUrl = 'itms-apps://apps.apple.com/us/app/google-authenticator/id388497605';
const androidStoreUrl =
  'https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2';

function goToAppStore() {
  const url = __ANDROID__ ? androidStoreUrl : appleStoreUrl;
  Linking.canOpenURL(url).then((supported) => {
    if (supported) {
      return Linking.openURL(url);
    } else {
      console.info('linking error');
    }
  });
}

function launchEmail() {
  Utils.goToEmail(I18n.t('auth.2faAndPasswordResetInstructionsLabel2'));
}

const Privacy = (props) => {
  if (!props.showPrivacy || props.needsMFASetup || props.needsMFACode) return null;

  return (
    <View style={styles.bottomContainer}>
      <PrivacyButton componentId={props.componentId} />
    </View>
  );
};

const ErrorView = (props) => {
  const errorMessage = props.errorMessage;
  const successMessage = props.successMessage;

  if (props.hideWhenNoValue && !errorMessage && !successMessage) return null;

  return (
    <View style={styles.errorContainer}>
      {!!errorMessage && <Text style={styles.errorMessage}>{errorMessage}</Text>}
      {!!successMessage && <Text style={styles.successMessage}>{successMessage}</Text>}
    </View>
  );
};

function mapStateToProps(state) {
  return {
    pendingTermsAccept: state.user.pendingTermsAccept,
  };
}
function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators({ acceptOrRejectAFTerms }, dispatch),
  };
}
const AuthContainer = memo(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )((props) => {
    const handleCancel = useCallback(() => {
      props.actions.acceptOrRejectAFTerms({ accept: false });
    }, []);
    const handleSave = useCallback(() => {
      props.actions.acceptOrRejectAFTerms({ accept: true });
    });
    const afConfirmationOpts = props.pendingTermsAccept
      ? {
          title: 'Accessing a U.S. Government System',
          description: I18n.t('airforce.terms'),
          onCancelled: handleCancel,
          onConfirmed: handleSave,
          reject: 'Exit',
          confirm: 'Enter',
          textStyle: {
            textAlign: 'left',
            maxWidth: 400,
            alignSelf: 'center',
            paddingVertical: 30,
            marginHorizontal: __WEB__ ? 70 : 0,
          },
        }
      : null;

    return (
      <Container
        style={styles.innerContainer}
        keyboardAvoidingScrollView={!__WEB__}
        contentContainerStyle={{ paddingHorizontal: 10 }}
        confirmationOpts={afConfirmationOpts}
      >
        {props.children}
      </Container>
    );
  })
);

const AuthForm = (props) => {
  function renderContents() {
    if (props.needsMFASetup)
      return (
        <SetupMFA
          {...props}
          onSubmit={props.onSubmitMFA}
          renderedError={<ErrorView {...props} hideWhenNoValue={true} />}
          handleNoAuthAppPress={goToAppStore}
        />
      );

    if (props.needsMFACode)
      return (
        <EnterMFA
          {...props}
          onSubmit={props.onSubmitMFA}
          renderedError={<ErrorView {...props} hideWhenNoValue={false} />}
          handleSupportEmailPress={launchEmail}
        />
      );

    return props.children;
  }

  if (__WEB__) {
    return <View style={styles.container}>{renderContents()}</View>;
  }

  return (
    <View style={styles.container}>
      <BackgroundImageContainer isMilitary={props.military}>
        <AuthContainer>
          <View style={[styles.iconContainer, { paddingTop: 120 }]}>
            <Image style={styles.brandingImage} source={require('../../../resources/icon.png')} />
          </View>

          {renderContents()}

          {!props.skipDisclaimerLinks && <Privacy {...props} />}
        </AuthContainer>
      </BackgroundImageContainer>
      {props.showActivityOverlay && <ActivityOverlay sizeToScreen={true} />}
    </View>
  );
};

const styles = StyleSheet.create({
  ...CommonStyles,
  innerContainer: {
    ...THEME.container,
    backgroundColor: 'transparent',
  },
});

module.exports = {
  AuthForm: memo(AuthForm),
  ErrorView: memo(ErrorView),
  AuthContainer,
};
